import pool from "./util/pool";
const { allocType, freeType } = pool;

const GL_SUBPIXEL_BITS = 0x0d50;
const GL_RED_BITS = 0x0d52;
const GL_GREEN_BITS = 0x0d53;
const GL_BLUE_BITS = 0x0d54;
const GL_ALPHA_BITS = 0x0d55;
const GL_DEPTH_BITS = 0x0d56;
const GL_STENCIL_BITS = 0x0d57;

const GL_ALIASED_POINT_SIZE_RANGE = 0x846d;
const GL_ALIASED_LINE_WIDTH_RANGE = 0x846e;

const GL_MAX_TEXTURE_SIZE = 0x0d33;
const GL_MAX_VIEWPORT_DIMS = 0x0d3a;
const GL_MAX_VERTEX_ATTRIBS = 0x8869;
const GL_MAX_VERTEX_UNIFORM_VECTORS = 0x8dfb;
const GL_MAX_VARYING_VECTORS = 0x8dfc;
const GL_MAX_COMBINED_TEXTURE_IMAGE_UNITS = 0x8b4d;
const GL_MAX_VERTEX_TEXTURE_IMAGE_UNITS = 0x8b4c;
const GL_MAX_TEXTURE_IMAGE_UNITS = 0x8872;
const GL_MAX_FRAGMENT_UNIFORM_VECTORS = 0x8dfd;
const GL_MAX_CUBE_MAP_TEXTURE_SIZE = 0x851c;
const GL_MAX_RENDERBUFFER_SIZE = 0x84e8;

const GL_VENDOR = 0x1f00;
const GL_RENDERER = 0x1f01;
const GL_VERSION = 0x1f02;
const GL_SHADING_LANGUAGE_VERSION = 0x8b8c;

const GL_MAX_TEXTURE_MAX_ANISOTROPY_EXT = 0x84ff;

const GL_MAX_COLOR_ATTACHMENTS_WEBGL = 0x8cdf;
const GL_MAX_DRAW_BUFFERS_WEBGL = 0x8824;

const GL_TEXTURE_2D = 0x0de1;
const GL_TEXTURE_CUBE_MAP = 0x8513;
const GL_TEXTURE_CUBE_MAP_POSITIVE_X = 0x8515;
const GL_TEXTURE0 = 0x84c0;
const GL_RGBA = 0x1908;
const GL_FLOAT = 0x1406;
const GL_UNSIGNED_BYTE = 0x1401;
const GL_FRAMEBUFFER = 0x8d40;
const GL_FRAMEBUFFER_COMPLETE = 0x8cd5;
const GL_COLOR_ATTACHMENT0 = 0x8ce0;
const GL_COLOR_BUFFER_BIT = 0x4000;

export default function(gl, extensions) {
  let maxAnisotropic = 1;
  if (extensions.ext_texture_filter_anisotropic) {
    maxAnisotropic = gl.getParameter(GL_MAX_TEXTURE_MAX_ANISOTROPY_EXT);
  }

  let maxDrawbuffers = 1;
  let maxColorAttachments = 1;
  if (extensions.webgl_draw_buffers) {
    maxDrawbuffers = gl.getParameter(GL_MAX_DRAW_BUFFERS_WEBGL);
    maxColorAttachments = gl.getParameter(GL_MAX_COLOR_ATTACHMENTS_WEBGL);
  }

  // detect if reading float textures is available (Safari doesn't support)
  let readFloat = !!extensions.oes_texture_float;
  if (readFloat) {
    const readFloatTexture = gl.createTexture();
    gl.bindTexture(GL_TEXTURE_2D, readFloatTexture);
    gl.texImage2D(GL_TEXTURE_2D, 0, GL_RGBA, 1, 1, 0, GL_RGBA, GL_FLOAT, null);

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(GL_FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      GL_FRAMEBUFFER,
      GL_COLOR_ATTACHMENT0,
      GL_TEXTURE_2D,
      readFloatTexture,
      0
    );
    gl.bindTexture(GL_TEXTURE_2D, null);

    if (gl.checkFramebufferStatus(GL_FRAMEBUFFER) !== GL_FRAMEBUFFER_COMPLETE)
      readFloat = false;
    else {
      gl.viewport(0, 0, 1, 1);
      gl.clearColor(1.0, 0.0, 0.0, 1.0);
      gl.clear(GL_COLOR_BUFFER_BIT);
      const pixels = allocType(GL_FLOAT, 4);
      gl.readPixels(0, 0, 1, 1, GL_RGBA, GL_FLOAT, pixels);

      if (gl.getError()) readFloat = false;
      else {
        gl.deleteFramebuffer(fbo);
        gl.deleteTexture(readFloatTexture);

        readFloat = pixels[0] === 1.0;
      }

      freeType(pixels);
    }
  }

  // detect non power of two cube textures support (IE doesn't support)
  const isIE =
    typeof navigator !== "undefined" &&
    (/MSIE/.test(navigator.userAgent) ||
      /Trident\//.test(navigator.appVersion) ||
      /Edge/.test(navigator.userAgent));

  let npotTextureCube = true;

  if (!isIE) {
    const cubeTexture = gl.createTexture();
    const data = allocType(GL_UNSIGNED_BYTE, 36);
    gl.activeTexture(GL_TEXTURE0);
    gl.bindTexture(GL_TEXTURE_CUBE_MAP, cubeTexture);
    gl.texImage2D(
      GL_TEXTURE_CUBE_MAP_POSITIVE_X,
      0,
      GL_RGBA,
      3,
      3,
      0,
      GL_RGBA,
      GL_UNSIGNED_BYTE,
      data
    );
    freeType(data);
    gl.bindTexture(GL_TEXTURE_CUBE_MAP, null);
    gl.deleteTexture(cubeTexture);
    npotTextureCube = !gl.getError();
  }

  return {
    // drawing buffer bit depth
    colorBits: [
      gl.getParameter(GL_RED_BITS),
      gl.getParameter(GL_GREEN_BITS),
      gl.getParameter(GL_BLUE_BITS),
      gl.getParameter(GL_ALPHA_BITS),
    ],
    depthBits: gl.getParameter(GL_DEPTH_BITS),
    stencilBits: gl.getParameter(GL_STENCIL_BITS),
    subpixelBits: gl.getParameter(GL_SUBPIXEL_BITS),

    // supported extensions
    extensions: Object.keys(extensions).filter(function(ext) {
      return !!extensions[ext];
    }),

    // max aniso samples
    maxAnisotropic: maxAnisotropic,

    // max draw buffers
    maxDrawbuffers: maxDrawbuffers,
    maxColorAttachments: maxColorAttachments,

    // point and line size ranges
    pointSizeDims: gl.getParameter(GL_ALIASED_POINT_SIZE_RANGE),
    lineWidthDims: gl.getParameter(GL_ALIASED_LINE_WIDTH_RANGE),
    maxViewportDims: gl.getParameter(GL_MAX_VIEWPORT_DIMS),
    maxCombinedTextureUnits: gl.getParameter(
      GL_MAX_COMBINED_TEXTURE_IMAGE_UNITS
    ),
    maxCubeMapSize: gl.getParameter(GL_MAX_CUBE_MAP_TEXTURE_SIZE),
    maxRenderbufferSize: gl.getParameter(GL_MAX_RENDERBUFFER_SIZE),
    maxTextureUnits: gl.getParameter(GL_MAX_TEXTURE_IMAGE_UNITS),
    maxTextureSize: gl.getParameter(GL_MAX_TEXTURE_SIZE),
    maxAttributes: gl.getParameter(GL_MAX_VERTEX_ATTRIBS),
    maxVertexUniforms: gl.getParameter(GL_MAX_VERTEX_UNIFORM_VECTORS),
    maxVertexTextureUnits: gl.getParameter(GL_MAX_VERTEX_TEXTURE_IMAGE_UNITS),
    maxVaryingVectors: gl.getParameter(GL_MAX_VARYING_VECTORS),
    maxFragmentUniforms: gl.getParameter(GL_MAX_FRAGMENT_UNIFORM_VECTORS),

    // vendor info
    glsl: gl.getParameter(GL_SHADING_LANGUAGE_VERSION),
    renderer: gl.getParameter(GL_RENDERER),
    vendor: gl.getParameter(GL_VENDOR),
    version: gl.getParameter(GL_VERSION),

    // quirks
    readFloat: readFloat,
    npotTextureCube: npotTextureCube,
  };
}
